import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Brain, FileText, CheckCircle, TrendingUp, Sparkles, ArrowRight, Zap, Target, Users, Code2, GraduationCap } from 'lucide-react';

/* ─── Animated Counter Hook ─── */
const useCounter = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let startTime = null;
          const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
            setCount(Math.floor(eased * end));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return [count, ref];
};

const Home = () => {
  const [count1, ref1] = useCounter(200, 2000);
  const [count2, ref2] = useCounter(4, 1500);
  const [count3, ref3] = useCounter(5, 1500);

  return (
    <div className="space-y-20">
      {/* ═══ Hero Section ═══ */}
      <div className="relative rounded-3xl overflow-hidden min-h-[500px] flex items-center">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-900 dark:via-purple-900 dark:to-pink-900" />
        <div className="absolute inset-0 opacity-30 dark:opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.5) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 50% 80%, rgba(120, 219, 255, 0.4) 0%, transparent 50%)`,
          }}
        />
        
        {/* Floating Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white/10 animate-float" />
          <div className="absolute top-1/3 right-16 w-20 h-20 rounded-2xl bg-white/10 animate-float-slow rotate-12" />
          <div className="absolute bottom-10 left-1/4 w-16 h-16 rounded-xl bg-white/10 animate-float" style={{ animationDelay: '3s' }} />
          <div className="absolute bottom-1/3 right-1/3 w-24 h-24 rounded-full bg-white/5 animate-morph" />
          {/* Grid overlay */}
          <div className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 px-8 py-20 md:px-16 lg:px-24 max-w-4xl animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white/90 text-sm font-medium mb-8 animate-slide-up-fade">
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span>Your Placement Journey Starts Here</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6 leading-[1.1] text-white font-display">
            Master. Prepare.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-blue-200 to-purple-300">
              Get Placed.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-white/70 mb-10 max-w-xl font-light leading-relaxed">
            Crack DSA, conquer aptitude, build a stellar resume, and track every milestone on your path to your dream company.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/dsa"
              className="group inline-flex items-center justify-center gap-2 bg-white text-indigo-700 font-bold px-8 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5 text-sm"
            >
              <Code2 className="w-5 h-5" />
              Start Solving
              <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/account"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 font-semibold px-8 py-4 rounded-2xl hover:bg-white/20 transition-all duration-300 hover:-translate-y-0.5 text-sm"
            >
              <TrendingUp className="w-5 h-5" />
              My Dashboard
            </Link>
          </div>
        </div>
      </div>

      {/* ═══ Stats Bar ═══ */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <StatItem ref_={ref1} count={count1} suffix="+" label="DSA Questions" icon={<Code2 className="w-6 h-6" />} color="indigo" />
        <StatItem ref_={ref2} count={count2} suffix="" label="Aptitude Categories" icon={<Brain className="w-6 h-6" />} color="purple" />
        <StatItem ref_={ref3} count={count3} suffix="" label="Resume Templates" icon={<FileText className="w-6 h-6" />} color="rose" />
      </div>

      {/* ═══ Feature Cards ═══ */}
      <div>
        <div className="text-center mb-12">
          <p className="text-sm font-bold text-indigo-500 dark:text-indigo-400 uppercase tracking-wider mb-3">Everything You Need</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white font-display">
            One Platform. Complete Prep.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8 stagger-children">
          <FeatureCard
            icon={<BookOpen className="w-7 h-7" />}
            title="DSA Preparation"
            description="200+ hand-picked coding problems organized by topic and difficulty. Track what you've solved and stay on top of your progress."
            link="/dsa"
            linkText="Start Solving"
            gradient="from-indigo-500 to-blue-500"
            iconBg="bg-indigo-100 dark:bg-indigo-900/40"
            iconColor="text-indigo-600 dark:text-indigo-400"
          />
          <FeatureCard
            icon={<Brain className="w-7 h-7" />}
            title="Aptitude Tests"
            description="Sharpen your quantitative, logical reasoning, and verbal skills with our extensive question bank featuring instant feedback."
            link="/aptitude"
            linkText="Take a Test"
            gradient="from-purple-500 to-violet-500"
            iconBg="bg-purple-100 dark:bg-purple-900/40"
            iconColor="text-purple-600 dark:text-purple-400"
          />
          <FeatureCard
            icon={<FileText className="w-7 h-7" />}
            title="Resume Building"
            description="Access professionally designed templates tailored for SDE, Web Dev, Data Science, and more. Download and customize instantly."
            link="/resume"
            linkText="Get Templates"
            gradient="from-rose-500 to-pink-500"
            iconBg="bg-rose-100 dark:bg-rose-900/40"
            iconColor="text-rose-600 dark:text-rose-400"
          />
        </div>
      </div>

      {/* ═══ Why PrepMaster ═══ */}
      <div className="glass-card p-8 md:p-12 relative overflow-hidden">
        {/* Subtle bg decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-gradient-to-br from-indigo-500/10 to-purple-500/10 blur-3xl pointer-events-none" />
        
        <div className="relative z-10">
          <div className="text-center mb-10">
            <p className="text-sm font-bold text-purple-500 dark:text-purple-400 uppercase tracking-wider mb-3">Why Students Love Us</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white font-display">
              Built for Serious Preparation
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            <WhyItem icon={<Target className="w-5 h-5" />} title="Curated Content" description="Every question is hand-picked for placement relevance" />
            <WhyItem icon={<Zap className="w-5 h-5" />} title="Real-time Tracking" description="See your progress update live as you solve" />
            <WhyItem icon={<GraduationCap className="w-5 h-5" />} title="Topic Mastery" description="Skill radar shows strengths and weak areas" />
            <WhyItem icon={<Users className="w-5 h-5" />} title="Community Driven" description="Problems sourced from real placement experiences" />
          </div>
        </div>
      </div>

      {/* ═══ CTA Banner ═══ */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600" />
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle at 30% 50%, rgba(255,255,255,0.2) 0%, transparent 50%)`,
          }}
        />
        <div className="relative z-10 px-8 py-14 md:py-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3 font-display">
              Ready to crack your next interview?
            </h3>
            <p className="text-white/70 text-lg max-w-md">
              Join thousands of students who are preparing smarter, not harder.
            </p>
          </div>
          <Link
            to="/auth"
            className="group inline-flex items-center gap-2 bg-white text-indigo-700 font-bold px-8 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5 text-sm flex-shrink-0"
          >
            <Sparkles className="w-5 h-5 text-amber-500" />
            Start Free Today
            <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
};

/* ─── Sub-Components ─── */

const StatItem = ({ ref_, count, suffix, label, icon, color }) => {
  const colors = {
    indigo: 'text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/40 border-indigo-200 dark:border-indigo-800/50',
    purple: 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/40 border-purple-200 dark:border-purple-800/50',
    rose: 'text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/40 border-rose-200 dark:border-rose-800/50',
  };
  return (
    <div ref={ref_} className="glass-card p-6 flex items-center gap-5 group hover:-translate-y-1 transition-all duration-300">
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border ${colors[color]} group-hover:scale-110 transition-transform duration-300`}>
        {icon}
      </div>
      <div>
        <h3 className="text-3xl font-extrabold text-gray-900 dark:text-white font-display tabular-nums">
          {count}{suffix}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{label}</p>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, link, linkText, gradient, iconBg, iconColor }) => (
  <div className="glass-card-glow p-8 group hover:-translate-y-2 transition-all duration-500 flex flex-col h-full">
    <div className={`w-14 h-14 ${iconBg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
      <span className={iconColor}>{icon}</span>
    </div>
    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2 font-display">
      {title}
      <CheckCircle className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
    </h3>
    <p className="text-gray-500 dark:text-gray-400 leading-relaxed mb-6 flex-1 text-sm">
      {description}
    </p>
    <Link
      to={link}
      className={`inline-flex items-center gap-1.5 text-sm font-semibold bg-gradient-to-r ${gradient} bg-clip-text text-transparent group-hover:gap-3 transition-all duration-300`}
    >
      {linkText}
      <ArrowRight className={`w-4 h-4 bg-gradient-to-r ${gradient} bg-clip-text`} style={{ color: 'inherit' }} />
    </Link>
  </div>
);

const WhyItem = ({ icon, title, description }) => (
  <div className="text-center p-6 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors duration-300 group">
    <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 dark:from-indigo-500/20 dark:to-purple-500/20 flex items-center justify-center mb-4 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform duration-300">
      {icon}
    </div>
    <h4 className="font-bold text-gray-900 dark:text-white mb-1.5 text-sm">{title}</h4>
    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
  </div>
);

export default Home;
